<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  inboxId,
  contentAttributes,
} = useMessageContext();
const { t } = useI18n();
const inboxGetter = useMapGetter('inboxes/getInbox');

const inbox = computed(() => inboxGetter.value(inboxId.value) || {});
const DEFAULT_TEMPLATE_BACKGROUND_COLOR = '#2f80ed';
const temporaryMessageLabel = 'Mensagem temporária';
const smsMessageLabel = 'SMS';

const variantBaseMap = {
  [MESSAGE_VARIANTS.AGENT]:
    '!bg-n-brand !text-white [&_.prose-bubble]:!text-white [&_.prose-bubble_*]:!text-white',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber border border-dashed border-n-amber-9 text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]:
    '!bg-n-slate-3 !text-n-slate-12 dark:!bg-n-slate-4 dark:!text-n-slate-11 [&_.prose-bubble]:!text-n-slate-12 dark:[&_.prose-bubble]:!text-n-slate-11 [&_.prose-bubble_*]:!text-n-slate-12 dark:[&_.prose-bubble_*]:!text-n-slate-11',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]:
    'ai-message-color-effect !bg-transparent !text-[#334155] [&_.prose-bubble]:!text-[#334155] [&_.prose-bubble_*]:!text-[#334155] [&_.prose-bubble]:!font-medium [&_.prose-bubble_*]:!font-medium',
  [MESSAGE_VARIANTS.CAMPAIGN]:
    'campaign-bubble !bg-[#4c1d95] !text-white border border-transparent [&_.prose-bubble]:!text-white [&_.prose-bubble_*]:!text-white',
  [MESSAGE_VARIANTS.TEMPLATE]:
    '!bg-[var(--message-bubble-bg)] !text-[var(--message-bubble-text)] [&_.prose-bubble]:!text-[var(--message-bubble-text)] [&_.prose-bubble_*]:!text-[var(--message-bubble-text)]',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const temporaryPrivateExpiresAt = computed(
  () =>
    contentAttributes.value?.expiresAt ||
    contentAttributes.value?.expires_at ||
    ''
);
const isTemporaryPrivateMessage = computed(
  () =>
    variant.value === MESSAGE_VARIANTS.PRIVATE &&
    Boolean(
      contentAttributes.value?.temporaryPrivate ||
        contentAttributes.value?.temporary_private
    ) &&
    Boolean(temporaryPrivateExpiresAt.value)
);
const currentTime = ref(Date.now());
let countdownTimer;

const remainingMilliseconds = computed(() =>
  Math.max(
    0,
    new Date(temporaryPrivateExpiresAt.value).getTime() - currentTime.value
  )
);
const expiryCountdown = computed(() => {
  const totalSeconds = Math.ceil(remainingMilliseconds.value / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h`;
  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':');
});

onMounted(() => {
  if (!isTemporaryPrivateMessage.value) return;

  currentTime.value = Date.now();
  countdownTimer = window.setInterval(() => {
    currentTime.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  if (countdownTimer) window.clearInterval(countdownTimer);
});

const messageClass = computed(() => {
  if (isTemporaryPrivateMessage.value) {
    return [
      'temporary-private-message border bg-n-ruby-2 text-n-ruby-12 dark:bg-n-ruby-3 [&_.prosemirror-mention-node]:font-semibold',
      orientationMap[orientation.value],
    ];
  }

  const classToApply = [variantBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const messageStyle = computed(() => {
  if (variant.value !== MESSAGE_VARIANTS.TEMPLATE) return {};

  const widgetColor =
    inbox.value.channel_type === INBOX_TYPES.WEB
      ? inbox.value.widget_color
      : null;
  const backgroundColor = widgetColor || DEFAULT_TEMPLATE_BACKGROUND_COLOR;
  const textColor = widgetColor ? getContrastingTextColor(widgetColor) : '#fff';

  return {
    '--message-bubble-bg': backgroundColor,
    '--message-bubble-text': textColor,
  };
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const isAiMessageColorEffect = computed(
  () => variant.value === MESSAGE_VARIANTS.BOT
);
const isSmsMessage = computed(
  () =>
    contentAttributes.value?.talkviaSms === true ||
    contentAttributes.value?.talkvia_sms === true
);

const metaClass = computed(() => {
  if (isTemporaryPrivateMessage.value) return 'text-n-ruby-12/60';
  if (variant.value === MESSAGE_VARIANTS.EMAIL) return 'px-3 pb-3';
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return 'text-n-amber-12/60';
  if (variant.value === MESSAGE_VARIANTS.BOT) return '!text-[#64748b]';
  if (
    [
      MESSAGE_VARIANTS.AGENT,
      MESSAGE_VARIANTS.CAMPAIGN,
      MESSAGE_VARIANTS.TEMPLATE,
    ].includes(variant.value)
  ) {
    return '!text-white/75';
  }

  if (variant.value === MESSAGE_VARIANTS.USER)
    return '!text-n-slate-11 dark:!text-white/75';

  return 'text-n-slate-11';
});

const replyPreviewClass = computed(() => {
  const readableText =
    '[&_.prose-bubble]:!text-current [&_.prose-bubble_*]:!text-current [&_.prose-bubble]:!leading-relaxed [&_.prose-bubble_*]:!leading-relaxed';

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return [
      'bg-n-solid-1 border-n-strong text-n-slate-12 hover:bg-n-solid-2 dark:bg-n-solid-2 dark:text-n-slate-12 dark:hover:bg-n-solid-3',
      readableText,
    ];
  }

  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return [
      'bg-black/5 border-[#94a3b8]/70 text-[#475569] hover:bg-black/10',
      readableText,
    ];
  }

  if (
    [
      MESSAGE_VARIANTS.AGENT,
      MESSAGE_VARIANTS.CAMPAIGN,
      MESSAGE_VARIANTS.TEMPLATE,
    ].includes(variant.value)
  ) {
    return [
      'bg-white/15 border-white/70 text-white/85 ring-1 ring-white/15 hover:bg-white/20',
      readableText,
    ];
  }

  return [
    'bg-black/10 border-white/40 text-white/85 hover:bg-black/15',
    readableText,
  ];
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
    :style="messageStyle"
  >
    <template v-if="isAiMessageColorEffect">
      <div class="ai-message-color-effect__glow" aria-hidden="true">
        <div class="ai-message-color-effect__gradient">
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
        </div>
      </div>

      <div class="ai-message-color-effect__edge" aria-hidden="true">
        <div class="ai-message-color-effect__gradient">
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
          <span class="ai-message-color-effect__color" />
        </div>
      </div>
    </template>

    <div :class="{ 'ai-message-color-effect__inner': isAiMessageColorEffect }">
      <div
        v-if="isSmsMessage"
        class="mb-2 inline-flex items-center gap-1.5 rounded-full border border-current/20 bg-black/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide"
      >
        <span class="i-lucide-message-square-text size-3.5" />
        <span>{{ smsMessageLabel }}</span>
      </div>
      <div
        v-if="isTemporaryPrivateMessage"
        class="temporary-private-message__timer mb-2.5 flex items-center justify-between gap-3 rounded-lg px-2.5 py-2"
      >
        <span class="inline-flex items-center gap-2 text-xs font-semibold">
          <span class="i-lucide-bomb size-4" />
          <span>{{ temporaryMessageLabel }}</span>
        </span>
        <span
          class="inline-flex items-center gap-1.5 rounded-full bg-n-ruby-4 px-2 py-1 font-mono text-[11px] font-semibold tabular-nums text-n-ruby-11"
        >
          <span class="i-lucide-clock-3 size-3" />
          {{ expiryCountdown }}
        </span>
      </div>
      <div
        v-if="inReplyTo"
        class="mb-2.5 rounded-lg border-l-2 px-3 py-2 shadow-sm cursor-pointer transition-colors"
        :class="replyPreviewClass"
        @click="scrollToMessage"
      >
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2 text-xs"
        />
      </div>
      <slot />
      <MessageMeta
        v-if="shouldShowMeta"
        :class="[flexOrientationClass, metaClass]"
        class="mt-2"
      />
    </div>
  </div>
</template>

<style scoped>
.temporary-private-message {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background-image: radial-gradient(
    circle at 100% 0%,
    rgb(244 63 94 / 16%),
    transparent 48%
  );
  border-color: rgb(244 63 94 / 42%);
  box-shadow:
    0 0 0 1px rgb(244 63 94 / 8%),
    0 10px 28px rgb(159 18 57 / 10%);
}

.temporary-private-message::before,
.temporary-private-message::after {
  position: absolute;
  pointer-events: none;
  content: '';
  z-index: 2;
}

.temporary-private-message::before {
  inset: 0;
  padding: 2px;
  border-radius: inherit;
  background: repeating-linear-gradient(
    115deg,
    rgb(244 63 94 / 38%) 0 7px,
    rgb(251 146 60 / 85%) 7px 10px,
    rgb(253 224 71 / 95%) 10px 12px,
    rgb(244 63 94 / 38%) 12px 19px
  );
  background-size: 40px 40px;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: temporary-private-fuse 1.8s linear infinite;
}

.temporary-private-message::after {
  top: 0;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: rgb(254 240 138);
  box-shadow:
    0 0 3px 1px rgb(255 255 255 / 90%),
    0 0 8px 3px rgb(251 146 60 / 90%),
    0 0 14px 5px rgb(244 63 94 / 55%);
  offset-path: inset(3px round 11px);
  animation: temporary-private-ember 4s linear infinite;
}

.temporary-private-message__timer {
  border: 1px solid rgb(244 63 94 / 20%);
  background: rgb(244 63 94 / 8%);
}

@keyframes temporary-private-fuse {
  to {
    background-position: 40px 0;
  }
}

@keyframes temporary-private-ember {
  to {
    offset-distance: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .temporary-private-message::before,
  .temporary-private-message::after {
    animation: none;
  }
}

.ai-message-color-effect {
  position: relative;
  isolation: isolate;
  background: transparent;
  padding: 0 !important;
  overflow: visible;

  --ai-card-bg: #ffffff;
  --ai-border-size: 2px;
}

.ai-message-color-effect::selection,
.ai-message-color-effect *::selection {
  background: rgb(66 133 244 / 0.24);
}

.campaign-bubble {
  box-shadow:
    0 0 0 1px rgb(167 139 250 / 0.26),
    0 12px 26px rgb(124 58 237 / 0.16),
    inset 0 0 16px rgb(196 181 253 / 0.12);
}

.ai-message-color-effect__glow,
.ai-message-color-effect__edge {
  position: absolute;
  border-radius: inherit;
  overflow: clip;
  pointer-events: none;
}

.ai-message-color-effect__glow {
  inset: -2px;
  z-index: -2;
  opacity: 0.78;
  filter: blur(7px);
  animation: ai-message-blur-linear 3.2s ease-in-out infinite;
}

.ai-message-color-effect__edge {
  inset: 0;
  z-index: -1;
  opacity: 0.72;
}

.ai-message-color-effect__gradient {
  position: absolute;
  inset: 0;
  scale: 10;
  transform-origin: center;
  animation: ai-message-gradient-spin 3.6s linear infinite;
  will-change: transform;
}

.ai-message-color-effect__color {
  position: absolute;
  inset: 0;
  opacity: 0;
  animation: ai-message-color-crossfade 8s ease-in-out infinite;
  will-change: opacity;
}

.ai-message-color-effect__color:nth-child(1) {
  background: conic-gradient(
    from 120deg,
    transparent 0,
    rgb(166 168 255 / 0) 14%,
    rgb(166 168 255 / 0.7) 20%,
    rgb(123 151 255 / 0.88) 28%,
    rgb(49 134 255 / 0.92) 35%,
    rgb(49 134 255 / 0.35) 42%,
    transparent 52%
  );
  animation-delay: 0s;
}

.ai-message-color-effect__color:nth-child(2) {
  background: conic-gradient(
    from 120deg,
    transparent 0,
    rgb(255 99 128 / 0) 14%,
    rgb(255 99 128 / 0.68) 20%,
    rgb(254 86 92 / 0.86) 28%,
    rgb(252 65 61 / 0.9) 35%,
    rgb(252 65 61 / 0.34) 42%,
    transparent 52%
  );
  animation-delay: 2s;
}

.ai-message-color-effect__color:nth-child(3) {
  background: conic-gradient(
    from 120deg,
    transparent 0,
    rgb(255 181 232 / 0) 14%,
    rgb(255 181 232 / 0.64) 20%,
    rgb(255 213 121 / 0.82) 28%,
    rgb(254 199 0 / 0.86) 35%,
    rgb(254 199 0 / 0.3) 42%,
    transparent 52%
  );
  animation-delay: 4s;
}

.ai-message-color-effect__color:nth-child(4) {
  background: conic-gradient(
    from 120deg,
    transparent 0,
    rgb(120 201 255 / 0) 14%,
    rgb(120 201 255 / 0.68) 20%,
    rgb(70 193 162 / 0.84) 28%,
    rgb(0 175 87 / 0.88) 35%,
    rgb(0 175 87 / 0.3) 42%,
    transparent 52%
  );
  animation-delay: 6s;
}

.ai-message-color-effect__inner {
  position: relative;
  z-index: 1;
  border: 1px solid rgb(148 163 184 / 22%);
  border-radius: inherit;
  background: var(--ai-card-bg);
  padding: 0.75rem 1rem;
}

.ai-message-color-effect .prose-bubble,
.ai-message-color-effect .prose-bubble * {
  color: #334155 !important;
}

@keyframes ai-message-gradient-spin {
  from {
    rotate: 0deg;
  }

  to {
    rotate: 360deg;
  }
}

@keyframes ai-message-blur-linear {
  0%,
  100% {
    opacity: 0.7;
    filter: blur(7px);
  }

  50% {
    opacity: 0.82;
    filter: blur(8px);
  }
}

@keyframes ai-message-color-crossfade {
  0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  25% {
    opacity: 1;
  }

  38% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-message-color-effect__gradient,
  .ai-message-color-effect__color,
  .ai-message-color-effect__glow {
    animation: none;
  }
}
</style>
